import {
	ClassNameProps,
	Column,
	Conditional,
	DateTime,
	Else,
	If,
	makeClassName,
	RelativeTimestamp,
	Row,
	StyleProps,
	Timestamp,
	TruncatedText,
} from "@hex-insights/core";
import { NewBadge } from "../../../../Components/Badge";
import { CircleLoadingPlaceholder, TextLineLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import { User, UserNameAndImageFragment } from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import { UserLink } from "../../../Links";
import { UserIcon } from "../../User/Icon";
import styles from "./styles.module.css";

export type AuthorTimestampViewProps = {
	author?: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	timestamp?: DateTime | null;
	isNew?: boolean;
	timestampVariant?: "relative" | "absolute";
	timestampContent?: React.ReactNode;
	timestampFallback?: React.ReactNode;
	iconSize?: number;
	iconClassName?: string;
	authorClassName?: string;
	timestampClassName?: string;
} & Partial<ClassNameProps & StyleProps>;

export function AuthorTimestampView({
	author,
	timestamp,
	isNew = false,
	timestampVariant = "relative",
	timestampContent,
	timestampFallback,
	iconSize = 40,
	iconClassName,
	authorClassName,
	timestampClassName,
	className,
	style,
}: AuthorTimestampViewProps) {
	return (
		<Row justify="spaced-start" className={className} style={style}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" style={{ overflow: "hidden" }}>
				{author && <UserIcon user={author} size={iconSize} className={iconClassName} />}

				<Column justify="spaced-start" verticalSpacing="0.125rem" style={{ overflow: "hidden" }}>
					<Row justify="spaced-start" align="center">
						{author && (
							<UserLink instance={author} className="link--no-text-decoration">
								<TruncatedText className={makeClassName(styles["author-timestamp-view__author"], authorClassName)}>
									{UserFormat.name(author)}
								</TruncatedText>
							</UserLink>
						)}
					</Row>

					<Conditional>
						<If condition={!!timestamp}>
							{timestamp && (
								<Conditional>
									<If condition={timestampVariant === "relative"}>
										<RelativeTimestamp
											value={timestamp}
											className={makeClassName(
												styles["author-timestamp-view__timestamp"] + " timestamp",
												timestampClassName,
											)}
										>
											{timestampContent}
										</RelativeTimestamp>
									</If>
									<Else>
										<Timestamp
											value={timestamp}
											className={makeClassName(
												styles["author-timestamp-view__timestamp"] + " timestamp",
												timestampClassName,
											)}
										>
											{timestampContent}
										</Timestamp>
									</Else>
								</Conditional>
							)}
						</If>
						<Else>{timestampFallback}</Else>
					</Conditional>
				</Column>
			</Row>

			<If condition={isNew}>
				<NewBadge size="small" />
			</If>
		</Row>
	);
}

export type AuthorTimestampViewLoadingPlaceholderProps = {
	iconSize?: number;
	withTimestamp?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function AuthorTimestampViewLoadingPlaceholder({
	iconSize = 40,
	withTimestamp = false,
	className,
	style,
}: AuthorTimestampViewLoadingPlaceholderProps) {
	return (
		<Row justify="spaced-start" className={className} style={style}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem" style={{ overflow: "hidden" }}>
				<CircleLoadingPlaceholder size={`${iconSize}px`} />

				<Column justify="spaced-start" verticalSpacing="0.125rem" style={{ overflow: "hidden" }}>
					<Row justify="spaced-start" align="center">
						<TextLineLoadingPlaceholder>Example Name X</TextLineLoadingPlaceholder>
					</Row>

					<If condition={withTimestamp}>
						<TextLineLoadingPlaceholder className={styles["author-timestamp-view__timestamp"]}>
							15 minutes ago
						</TextLineLoadingPlaceholder>
					</If>
				</Column>
			</Row>
		</Row>
	);
}
